import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Grid,
  IconButton,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";

const PublicTable = () => {
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [filters, setFilters] = useState({
    searchTitle: "",
    searchAuthor: "",
    searchGender: "",
    searchPseudonym: "",
    searchAnonymous: "",
    searchTitleKeyword: "",
    searchSource: "",
    searchSourceGenre: "",
    searchWorkGenre: "",
    searchTranslator: "",
    searchYear: "",
    searchPlace: "",
    searchPrinter: "",
    searchDedicatee: "",
    searchParatexts: "",
    searchSummaryKeywords: "",
  });

  const initialFilters = {
    searchTitle: "",
    searchAuthor: "",
    searchGender: "",
    searchPseudonym: "",
    searchAnonymous: "",
    searchTitleKeyword: "",
    searchSource: "",
    searchSourceGenre: "",
    searchWorkGenre: "",
    searchTranslator: "",
    searchYear: "",
    searchPlace: "",
    searchPrinter: "",
    searchDedicatee: "",
    searchParatexts: "",
    searchSummaryKeywords: "",
  };

  useEffect(() => {
    const fetchEntries = async () => {
      const querySnapshot = await getDocs(collection(db, "entries"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEntries(data);
      setFilteredEntries(data);
    };

    fetchEntries();
  }, []);

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setFilteredEntries(entries); // restablecer los resultados filtrados a todas las entradas
  };

  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const EXCEL_EXTENSION = ".xlsx";

  const handleExport = () => {
    const exportData = filteredEntries.map((entry) => ({
      Title: entry.Title.full_title,
      Author: entry.Author.name,
      Gender_Attribution: entry.Author.gender_attribution,
      Pseudonym: entry.Author.pseudonym,
      Anonymous: entry.Author.anonymous || "",
      Title_Keywords: Object.keys(entry.Title.title_subtitle_keyword)
        .filter((k) => entry.Title.title_subtitle_keyword[k])
        .join(", "),
      Source_Title: entry.Source.main_source_1.title,
      Source_Genre: entry.Source.main_source_1.genre,
      Work_Genre: Object.keys(entry.Genre_subgenre)
        .filter((k) => entry.Genre_subgenre[k])
        .join(", "),
      Translator: entry.Translation.translator_name,
      Year: entry.Bibliographical_details.year_of_first_edition,
      Place: entry.Bibliographical_details.place_of_publication,
      Printer: entry.Bibliographical_details.printer,
      Dedicatees: Array.isArray(entry.Dedicatee_s)
        ? entry.Dedicatee_s.filter((d) => d.name)
            .map((d) => `${d.name} (${d.gender || "Unknown"})`)
            .join(", ")
        : Object.values(entry.Dedicatee_s || {}).join(", "),
      Paratexts: Object.keys(entry.Paratexts)
        .filter((k) => entry.Paratexts[k])
        .join(", "),
      Summary_Keywords: Object.keys(entry.Summary.keywords)
        .filter((k) => entry.Summary.keywords[k])
        .join(", "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Entries");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: EXCEL_TYPE });
    saveAs(data, `filtered_entries_${new Date().getTime()}${EXCEL_EXTENSION}`);
  };

  const handleSearch = () => {
    let filtered = entries;

    if (filters.searchTitle) {
      filtered = filtered.filter((entry) =>
        entry.Title.full_title
          .toLowerCase()
          .includes(filters.searchTitle.toLowerCase()),
      );
    }

    if (filters.searchAuthor) {
      filtered = filtered.filter((entry) =>
        entry.Author.name
          .toLowerCase()
          .includes(filters.searchAuthor.toLowerCase()),
      );
    }

    if (filters.searchGender) {
      filtered = filtered.filter((entry) =>
        entry.Author.gender_attribution
          .toLowerCase()
          .includes(filters.searchGender.toLowerCase()),
      );
    }

    if (filters.searchPseudonym) {
      filtered = filtered.filter((entry) =>
        entry.Author.pseudonym
          .toLowerCase()
          .includes(filters.searchPseudonym.toLowerCase()),
      );
    }

    if (filters.searchAnonymous) {
      filtered = filtered.filter(
        (entry) =>
          entry.Author.anonymous &&
          entry.Author.anonymous
            .toLowerCase()
            .includes(filters.searchAnonymous.toLowerCase()),
      );
    }

    if (filters.searchTitleKeyword) {
      filtered = filtered.filter(
        (entry) =>
          entry.Title.title_subtitle_keyword[
            filters.searchTitleKeyword.toLowerCase()
          ],
      );
    }

    if (filters.searchSource) {
      filtered = filtered.filter((entry) =>
        entry.Source.main_source_1.title
          .toLowerCase()
          .includes(filters.searchSource.toLowerCase()),
      );
    }

    if (filters.searchSourceGenre) {
      filtered = filtered.filter((entry) =>
        entry.Source.main_source_1.genre
          .toLowerCase()
          .includes(filters.searchSourceGenre.toLowerCase()),
      );
    }

    if (filters.searchWorkGenre) {
      filtered = filtered.filter(
        (entry) => entry.Genre_subgenre[filters.searchWorkGenre.toLowerCase()],
      );
    }

    if (filters.searchTranslator) {
      filtered = filtered.filter((entry) =>
        entry.Translation.translator_name
          .toLowerCase()
          .includes(filters.searchTranslator.toLowerCase()),
      );
    }

    if (filters.searchYear) {
      filtered = filtered.filter((entry) =>
        entry.Bibliographical_details.year_of_first_edition.includes(
          filters.searchYear,
        ),
      );
    }

    if (filters.searchPlace) {
      filtered = filtered.filter((entry) =>
        entry.Bibliographical_details.place_of_publication
          .toLowerCase()
          .includes(filters.searchPlace.toLowerCase()),
      );
    }

    if (filters.searchPrinter) {
      filtered = filtered.filter((entry) =>
        entry.Bibliographical_details.printer
          .toLowerCase()
          .includes(filters.searchPrinter.toLowerCase()),
      );
    }

    if (filters.searchDedicatee) {
      filtered = filtered.filter((entry) =>
        Object.values(entry.Dedicatee_s).some((dedicatee) =>
          dedicatee
            .toLowerCase()
            .includes(filters.searchDedicatee.toLowerCase()),
        ),
      );
    }

    if (filters.searchParatexts) {
      filtered = filtered.filter((entry) =>
        Object.values(entry.Paratexts).some(
          (paratext) =>
            paratext &&
            paratext
              .toString()
              .toLowerCase()
              .includes(filters.searchParatexts.toLowerCase()),
        ),
      );
    }

    if (filters.searchSummaryKeywords) {
      filtered = filtered.filter(
        (entry) =>
          entry.Summary.keywords[filters.searchSummaryKeywords.toLowerCase()],
      );
    }

    setFilteredEntries(filtered);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const getSortValue = (entry, key) => {
    if (key === "title") return entry.Title?.full_title || "";
    if (key === "author") return entry.Author?.name || "";
    if (key === "gender") return entry.Author?.gender_attribution || "";
    if (key === "year") {
      const year = Number(entry.Bibliographical_details?.year_of_first_edition);
      return Number.isFinite(year) ? year : "";
    }
    if (key === "place") return entry.Bibliographical_details?.place_of_publication || "";
    return "";
  };

  const compareValues = (a, b) => {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";

    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === "number" && typeof b === "number") {
      return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (!sortBy) return 0;
    const compared = compareValues(getSortValue(a, sortBy), getSortValue(b, sortBy));
    return sortDirection === "asc" ? compared : -compared;
  });

  const handleRequestSort = (columnKey) => {
    const isSameColumn = sortBy === columnKey;
    if (isSameColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ color: "#1976d2", fontWeight: "bold" }}
        >
          Women's Early Printed Fiction Database
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Export to Excel
          </Button>
        </Stack>

        <Collapse in={showFilters}>
          <Card sx={{ mb: 3, backgroundColor: "#fafafa" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Search Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Title"
                    variant="outlined"
                    size="small"
                    value={filters.searchTitle}
                    onChange={(e) =>
                      setFilters({ ...filters, searchTitle: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Author"
                    variant="outlined"
                    size="small"
                    value={filters.searchAuthor}
                    onChange={(e) =>
                      setFilters({ ...filters, searchAuthor: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Gender"
                    variant="outlined"
                    size="small"
                    value={filters.searchGender}
                    onChange={(e) =>
                      setFilters({ ...filters, searchGender: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Pseudonym"
                    variant="outlined"
                    size="small"
                    value={filters.searchPseudonym}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        searchPseudonym: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Source"
                    variant="outlined"
                    size="small"
                    value={filters.searchSource}
                    onChange={(e) =>
                      setFilters({ ...filters, searchSource: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Year"
                    variant="outlined"
                    size="small"
                    value={filters.searchYear}
                    onChange={(e) =>
                      setFilters({ ...filters, searchYear: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Place"
                    variant="outlined"
                    size="small"
                    value={filters.searchPlace}
                    onChange={(e) =>
                      setFilters({ ...filters, searchPlace: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Translator"
                    variant="outlined"
                    size="small"
                    value={filters.searchTranslator}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        searchTranslator: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Dedicatee"
                    variant="outlined"
                    size="small"
                    value={filters.searchDedicatee}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        searchDedicatee: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>
      </Paper>

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 560 }} size="small">
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortBy === "title"}
                  direction={sortBy === "title" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("title")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortBy === "author"}
                  direction={sortBy === "author" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("author")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortBy === "gender"}
                  direction={sortBy === "gender" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("gender")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Gender
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortBy === "year"}
                  direction={sortBy === "year" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("year")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Year
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortBy === "place"}
                  direction={sortBy === "place" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("place")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Place
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {entry.Title.full_title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.Author.name || "Unknown"}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{entry.Author.gender_attribution}</TableCell>
                  <TableCell>
                    {entry.Bibliographical_details.year_of_first_edition}
                  </TableCell>
                  <TableCell>
                    {entry.Bibliographical_details.place_of_publication}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        component={Link}
                        to={`/detail/${entry.id}`}
                        size="small"
                        color="primary"
                        title="View Details"
                        sx={{ padding: "4px" }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      {entry.Bibliographical_details?.pdf_url && (
                        <IconButton
                          component="a"
                          href={entry.Bibliographical_details.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          color="error"
                          title="Open PDF"
                          sx={{ padding: "4px" }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredEntries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default PublicTable;
